import { useMemo } from 'react'

/**
 * Derive a normalized list of pairs the current user belongs to.
 *
 * The backend has no first-class "pair" entity. The data lives in two
 * places: the user's manager lives on `me.managerId / managerName`, and
 * for managers the list of reports comes from the `useTeam()` hook.
 *
 * Each entry has the shape:
 *   { pairId, reportId, managerId, reportName, managerName, role }
 * where `role` is 'report' or 'manager' from the current user's POV.
 *
 * Returned list is stable across renders so it can be used as a
 * dependency in the page's data hooks.
 */
export function usePairs(me, reports = []) {
  return useMemo(() => {
    const pairs = []
    if (!me?.userId) return pairs

    // Report side: my manager (if I have one)
    if (me.managerId) {
      pairs.push({
        pairId: `${me.userId}::${me.managerId}`,
        reportId: me.userId,
        managerId: me.managerId,
        reportName: me.name || 'You',
        managerName: me.managerName || 'Your manager',
        role: 'report',
      })
    }

    // Manager side: each of my reports
    for (const report of reports) {
      if (!report?.reportId) continue
      pairs.push({
        pairId: `${report.reportId}::${me.userId}`,
        reportId: report.reportId,
        managerId: me.userId,
        reportName: report.reportName || 'Report',
        managerName: me.name || 'You',
        role: 'manager',
      })
    }

    return pairs
  }, [me, reports])
}
